import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Settings, Clock, Calendar, ToggleLeft, ToggleRight, Save, Edit, Globe } from 'lucide-react';
import { TimeSettingsDialog } from '@/components/TimeSettingsDialog';
import { COMMON_TIMEZONES, formatSwitchTime } from '@/lib/propertyTime';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PropertiesManagement() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [freeChairDialogOpen, setFreeChairDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [timeProperty, setTimeProperty] = useState(null);
  const [freeChairSettings, setFreeChairSettings] = useState({
    free_chair_advance_hours: 0,
    free_chair_cutoff_time: '23:59',
    free_chair_enabled: true
  });
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    time_zone: 'America/New_York'
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchFreeChairSettings = async (propertyId) => {
    try {
      const response = await axios.get(`${API_URL}/properties/${propertyId}/free-chair-settings`);
      setFreeChairSettings(response.data);
    } catch (error) {
      console.error('Failed to load free chair settings:', error);
      // Use defaults
      setFreeChairSettings({
        free_chair_advance_hours: 0,
        free_chair_cutoff_time: '23:59',
        free_chair_enabled: true
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/properties`, formData);
      toast.success('Property created successfully');
      setDialogOpen(false);
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        time_zone: 'America/New_York'
      });
      fetchProperties();
    } catch (error) {
      toast.error('Failed to create property');
    }
  };

  const handleFreeChairSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/properties/${selectedProperty.id}/free-chair-settings`,
        {
          free_chair_advance_hours: parseInt(freeChairSettings.free_chair_advance_hours),
          free_chair_cutoff_time: freeChairSettings.free_chair_cutoff_time,
          free_chair_enabled: freeChairSettings.free_chair_enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Free chair settings updated');
      setFreeChairDialogOpen(false);
      fetchProperties();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const openFreeChairDialog = (property) => {
    setSelectedProperty(property);
    fetchFreeChairSettings(property.id);
    setFreeChairDialogOpen(true);
  };

  // Helper to format advance hours for display
  const formatAdvanceHours = (hours) => {
    if (hours === 0) return 'Same-day only';
    if (hours < 24) return `${hours} hours ahead`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days} day${days > 1 ? 's' : ''} ahead`;
    return `${days}d ${remainingHours}h ahead`;
  };

  return (
    <AdminLayout title="Properties Management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#1C1917]">Properties</h1>
          <p className="text-[#78716C] mt-1">Manage resort properties and free chair settings</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#9F1239] hover:bg-[#881337] text-white"
              data-testid="btn-add-property"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">Add New Property</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Property Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="input-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    data-testid="input-address"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                    data-testid="input-state"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">Zip Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    required
                    data-testid="input-zip"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    data-testid="input-country"
                  />
                </div>
                <div>
                  <Label htmlFor="time_zone">Timezone</Label>
                  <select
                    id="time_zone"
                    value={formData.time_zone}
                    onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E7E5E4] rounded-sm"
                  >
                    {COMMON_TIMEZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
                  </select>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#9F1239] hover:bg-[#881337]" data-testid="btn-submit">
                Create Property
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {properties.map((property) => (
            <Card key={property.id} className="bg-white border-[#E7E5E4] shadow-sm" data-testid={`property-${property.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl font-serif text-[#1C1917]">{property.name}</CardTitle>
                    <CardDescription className="mt-1">
                      {property.city}, {property.state} {property.zip_code}
                    </CardDescription>
                  </div>
                  <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTimeProperty(property)}
                    data-testid={`btn-time-settings-${property.id}`}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Time & Booking Window
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openFreeChairDialog(property)}
                    data-testid={`btn-free-chair-settings-${property.id}`}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Free Chair Settings
                  </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm text-[#78716C]">
                    <p>{property.address}</p>
                    <p className="mt-1" data-testid={`property-tz-${property.id}`}>Timezone: {property.time_zone || 'America/New_York'}</p>
                    <p className="mt-1" data-testid={`property-switch-${property.id}`}>Next-day switch: {formatSwitchTime(property.next_day_switch_time)}</p>
                  </div>
                  
                  {/* Free Chair Settings Summary */}
                  <div className="bg-[#F5F5F4] p-3 rounded-sm mt-4">
                    <h4 className="text-sm font-medium text-[#1C1917] flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-[#9F1239]" />
                      Free Chair Booking Rules
                    </h4>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <p className="text-[#A8A29E] uppercase tracking-wide">Status</p>
                        <p className={`font-medium ${property.free_chair_enabled !== false ? 'text-green-700' : 'text-red-700'}`}>
                          {property.free_chair_enabled !== false ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#A8A29E] uppercase tracking-wide">Advance</p>
                        <p className="font-medium text-[#1C1917]">
                          {formatAdvanceHours(property.free_chair_advance_hours || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#A8A29E] uppercase tracking-wide">Cutoff</p>
                        <p className="font-medium text-[#1C1917]">
                          {property.free_chair_cutoff_time || '23:59'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TimeSettingsDialog
        property={timeProperty}
        open={!!timeProperty}
        onOpenChange={(o) => !o && setTimeProperty(null)}
        onSaved={fetchProperties}
      />

      {/* Free Chair Settings Dialog */}
      <Dialog open={freeChairDialogOpen} onOpenChange={setFreeChairDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#9F1239]" />
              Free Chair Settings
            </DialogTitle>
          </DialogHeader>
          {selectedProperty && (
            <form onSubmit={handleFreeChairSubmit} className="space-y-5">
              <p className="text-sm text-[#78716C]">
                Configure booking rules for complimentary chairs at <strong>{selectedProperty.name}</strong>
              </p>

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#F5F5F4] rounded-sm">
                <div>
                  <Label className="text-base font-medium">Free Chair Booking</Label>
                  <p className="text-sm text-[#78716C] mt-0.5">
                    Allow guests to book complimentary chairs
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFreeChairSettings({
                    ...freeChairSettings,
                    free_chair_enabled: !freeChairSettings.free_chair_enabled
                  })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    freeChairSettings.free_chair_enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    freeChairSettings.free_chair_enabled ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Advance Booking Window */}
              <div>
                <Label htmlFor="advance_hours" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#9F1239]" />
                  Advance Booking Window
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    id="advance_hours"
                    type="number"
                    min="0"
                    max="168"
                    value={freeChairSettings.free_chair_advance_hours}
                    onChange={(e) => setFreeChairSettings({
                      ...freeChairSettings,
                      free_chair_advance_hours: e.target.value
                    })}
                    className="w-24"
                    disabled={!freeChairSettings.free_chair_enabled}
                  />
                  <span className="text-sm text-[#78716C]">hours before reservation date</span>
                </div>
                <p className="text-xs text-[#A8A29E] mt-1">
                  Set to 0 for same-day only. Set to 24 to allow booking tomorrow starting at midnight.
                </p>
              </div>

              {/* Cutoff Time */}
              <div>
                <Label htmlFor="cutoff_time" className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#9F1239]" />
                  Same-Day Cutoff Time
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    id="cutoff_time"
                    type="time"
                    value={freeChairSettings.free_chair_cutoff_time}
                    onChange={(e) => setFreeChairSettings({
                      ...freeChairSettings,
                      free_chair_cutoff_time: e.target.value
                    })}
                    className="w-32"
                    disabled={!freeChairSettings.free_chair_enabled}
                  />
                  <span className="text-sm text-[#78716C]">({selectedProperty.time_zone || 'America/New_York'})</span>
                </div>
                <p className="text-xs text-[#A8A29E] mt-1">
                  No free chair bookings accepted after this time for the current day.
                </p>
              </div>

              {/* Preview */}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
                <p className="text-sm text-blue-800">
                  <strong>Current Rule:</strong>{' '}
                  {freeChairSettings.free_chair_enabled ? (
                    <>
                      Guests can book free chairs{' '}
                      {freeChairSettings.free_chair_advance_hours == 0 
                        ? 'for today only' 
                        : `up to ${freeChairSettings.free_chair_advance_hours} hours in advance`}
                      , until {freeChairSettings.free_chair_cutoff_time} on the day of booking.
                    </>
                  ) : (
                    'Free chair booking is currently disabled.'
                  )}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setFreeChairDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#9F1239] hover:bg-[#881337]">
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
